import { useEffect, useRef } from 'react';
import type { Line } from '../../../shared/types';

/**
 * Every move of the line, with the board's current position marked.
 *
 * Two things this fixes. Stepping through a line was a one-way conveyor with no
 * way to see where you were in it, and no way to get back to a move whose
 * reasoning you wanted to read again. And a variation gave no sense of where it
 * *started*: the first moves of every Italian line are the same, so the part
 * worth attending to is whatever comes after them.
 *
 * The shared opening moves are therefore separated from the rest, with the
 * branch point marked. Moves already played are solid, the rest are dimmed, so
 * the list doubles as a progress indicator.
 *
 * Clicking seeks — in Study only. In Practice that would skip the test, so the
 * list becomes read-only and shows only what has been played.
 */
export default function MoveList({
  line,
  ply,
  onSeek,
  canSeek,
  /** How many opening plies this line shares with its opening's headline. */
  trunkPlies,
}: {
  line: Line;
  ply: number;
  onSeek: (ply: number) => void;
  canSeek: boolean;
  trunkPlies: number;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const rowsRef = useRef<HTMLOListElement>(null);

  // Keep the current move in view, scrolling *only* this list.
  //
  // scrollIntoView looks innocent and is not: it scrolls every scrollable
  // ancestor, so once the page itself became a scroll container this quietly
  // started dragging the whole page down whenever the current move changed --
  // most visibly on the last move, where finishing a line jumped the view to
  // somewhere the user had not asked to go. Setting scrollTop on the one
  // container that should move cannot reach past itself.
  useEffect(() => {
    const el = activeRef.current;
    const box = rowsRef.current;
    if (!el || !box) return;
    const top = el.offsetTop - box.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight;
  }, [ply]);

  const rows: { number: number; white?: MoveCell; black?: MoveCell }[] = [];
  line.moves.forEach((m, i) => {
    const cell: MoveCell = { san: m.san, index: i };
    const moveNumber = Math.floor(i / 2) + 1;
    const row = rows[rows.length - 1];
    if (i % 2 === 0) rows.push({ number: moveNumber, white: cell });
    else if (row) row.black = cell;
    else rows.push({ number: moveNumber, black: cell });
  });

  // The ply at which this line leaves the shared opening moves.
  const branchAt = Math.min(trunkPlies, line.moves.length);

  function cellClass(index: number) {
    return [
      'movelist-move',
      index < ply ? 'movelist-move--played' : 'movelist-move--upcoming',
      index === ply - 1 ? 'movelist-move--current' : '',
      index >= branchAt ? 'movelist-move--branch' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  function cell(c: MoveCell | undefined) {
    if (!c) return <span className="movelist-move movelist-move--empty">…</span>;
    // In Practice, moves you have not reached yet would be the answers.
    const hidden = !canSeek && c.index >= ply;
    if (hidden) return <span className="movelist-move movelist-move--hidden">•••</span>;
    return (
      <button
        type="button"
        ref={c.index === ply - 1 ? activeRef : undefined}
        className={cellClass(c.index)}
        disabled={!canSeek}
        onClick={() => onSeek(c.index + 1)}
        title={canSeek ? `Go to ${c.san}` : undefined}
      >
        {c.san}
      </button>
    );
  }

  return (
    <div className="movelist">
      <div className="movelist-head">
        <span>Moves</span>
        <span className="muted">
          {Math.min(ply, line.moves.length)} / {line.moves.length}
        </span>
      </div>
      <ol className="movelist-rows" ref={rowsRef}>
        {rows.map((row) => (
          <li key={row.number} className="movelist-row">
            <span className="movelist-num">{row.number}.</span>
            {cell(row.white)}
            {cell(row.black)}
          </li>
        ))}
      </ol>
      {branchAt > 0 && branchAt < line.moves.length && (
        <p className="muted movelist-note">
          The first {branchAt} {branchAt === 1 ? 'move' : 'moves'} are the opening itself; this
          variation starts at <strong>{line.moves[branchAt]?.san}</strong>.
        </p>
      )}
    </div>
  );
}

interface MoveCell {
  san: string;
  index: number;
}
